export const CHILE_TIME_ZONE = 'America/Santiago';

const DAY_PATTERN = /^(\d{4})-(\d{2})-(\d{2})$/;
const SLASH_DAY_PATTERN = /^(\d{2})\/(\d{2})\/(\d{4})$/;
const DASHED_DAY_PATTERN = /^(\d{2})-(\d{2})-(\d{4})$/;

const offsetNameFormat = new Intl.DateTimeFormat('en-US', {
  timeZone: CHILE_TIME_ZONE,
  timeZoneName: 'short'
});

const chileDayFormat = new Intl.DateTimeFormat('en-CA', {
  timeZone: CHILE_TIME_ZONE,
  year: 'numeric',
  month: '2-digit',
  day: '2-digit'
});

const validateDayParts = (year, month, date, raw) => {
  if (month < 1 || month > 12 || date < 1 || date > 31) {
    throw new Error(`calendarDay: invalid calendar day ${JSON.stringify(raw)}`);
  }
  const daysInMonth = new Date(Date.UTC(year, month, 0)).getUTCDate();
  if (date > daysInMonth) {
    throw new Error(`calendarDay: invalid calendar day ${JSON.stringify(raw)}`);
  }
  return { year, month, day: date };
};

const parseDayParts = (day) => {
  const match = DAY_PATTERN.exec(day);
  if (!match) {
    throw new Error(
      `calendarDay: expected calendar day "YYYY-MM-DD", got ${JSON.stringify(day)}`
    );
  }
  return validateDayParts(
    Number(match[1]),
    Number(match[2]),
    Number(match[3]),
    day
  );
};

// DD-MM-YYYY strict parse (BICE fechaExclusion shape): same validation,
// never routed through the host-dependent Date parser.
const parseDashedDayParts = (day) => {
  const match = DASHED_DAY_PATTERN.exec(day);
  if (!match) {
    throw new Error(
      `calendarDay: expected calendar day "DD-MM-YYYY", got ${JSON.stringify(day)}`
    );
  }
  return validateDayParts(
    Number(match[3]),
    Number(match[2]),
    Number(match[1]),
    day
  );
};

// DD/MM/YYYY strict parse: mirrors the backend helper so slash dates never
// reach the host-dependent Date parser (which would read 05/04 as May 4
// or reject day values over 12 depending on engine).
const parseSlashDayParts = (day) => {
  const match = SLASH_DAY_PATTERN.exec(day);
  if (!match) {
    throw new Error(
      `calendarDay: expected calendar day "DD/MM/YYYY", got ${JSON.stringify(day)}`
    );
  }
  return validateDayParts(
    Number(match[3]),
    Number(match[2]),
    Number(match[1]),
    day
  );
};

// Offset of America/Santiago at a UTC instant, in ms. Positive east of
// UTC. Read straight from the Intl short zone name (e.g. "GMT-4"), not
// from wall-clock parts: some engines report midnight as hour 24, and
// the 24:00 label is ambiguous between start-of-day (V8/ICU on Node 14)
// and end-of-prior-day. The zone name has no such ambiguity.
const offsetAt = (utcMs) => {
  const part = offsetNameFormat
    .formatToParts(new Date(utcMs))
    .find((item) => item.type === 'timeZoneName');
  if (!part) {
    throw new Error(
      'calendarDay: Intl omitted the timezone name when resolving Chile time'
    );
  }
  const match = /^GMT([+-])(\d{1,2})(?::(\d{2}))?$/.exec(part.value);
  if (!match) {
    throw new Error(
      `calendarDay: unparseable GMT offset ${JSON.stringify(part.value)}`
    );
  }
  const magnitudeMs =
    (Number(match[2]) * 60 + Number(match[3] || 0)) * 60 * 1000;
  // GMT-4 (west) resolves to -4h so wallAsUtc - offset lands on 04:00Z.
  return match[1] === '+' ? magnitudeMs : -magnitudeMs;
};

const chileMidnightUtcMs = (year, month, day) => {
  const wallAsUtc = Date.UTC(year, month - 1, day, 0, 0, 0);
  // Fixed-point iteration: utc converges when the offset stops changing.
  // On spring-forward gap days midnight does not exist, so the iterates
  // 2-cycle between the pre-gap and post-gap candidates; the stated gap
  // policy resolves forward (later instant), matching dayjs tz gap
  // shifting and the backend helper. Never settle by iteration parity.
  let utc = wallAsUtc;
  const seen = [utc];
  for (let i = 0; i < 10; i += 1) {
    const next = wallAsUtc - offsetAt(utc);
    if (next === utc) return utc;
    const cycleStart = seen.indexOf(next);
    if (cycleStart !== -1) {
      return Math.max(...seen.slice(cycleStart));
    }
    seen.push(next);
    utc = next;
  }
  throw new Error(
    `calendarDay: midnight did not converge for ${year}-${month}-${day}`
  );
};

const chileDayPartsFromUtcMs = (utcMs) => {
  const parts = chileDayFormat.formatToParts(new Date(utcMs));
  const get = (type) => {
    const part = parts.find((item) => item.type === type);
    if (!part) {
      throw new Error(
        `calendarDay: Intl omitted "${type}" when resolving Chile day`
      );
    }
    return part.value;
  };
  return {
    year: Number(get('year')),
    month: Number(get('month')),
    day: Number(get('day'))
  };
};

const padTo2Digits = (num) => String(num).padStart(2, '0');

// Parses a typed "YYYY-MM-DD" calendar day as Chile-midnight ISO.
// Pure: explicit America/Santiago, no host-local logic, no mutation.
export const parseDayToIso = (day) => {
  if (typeof day !== 'string') {
    throw new Error(
      `calendarDay: expected calendar day "YYYY-MM-DD", got ${JSON.stringify(day)}`
    );
  }
  // Whitespace padding is not a format violation: trim once, then demand
  // an exact strict-format match (same rule as the backend helper).
  const { year, month, day: date } = parseDayParts(day.trim());
  return new Date(chileMidnightUtcMs(year, month, date)).toISOString();
};

const instantMsFromValue = (value) => {
  if (value === null || value === undefined || value === '') {
    throw new Error('calendarDay: picker value is empty');
  }
  if (typeof value === 'string') {
    // Trim once so padded date-only strings hit the strict branches
    // instead of the host-dependent Date parser.
    const text = value.trim();
    if (text === '') {
      throw new Error('calendarDay: picker value is empty');
    }
    if (DAY_PATTERN.test(text)) {
      const parts = parseDayParts(text);
      return chileMidnightUtcMs(parts.year, parts.month, parts.day);
    }
    if (SLASH_DAY_PATTERN.test(text)) {
      const parts = parseSlashDayParts(text);
      return chileMidnightUtcMs(parts.year, parts.month, parts.day);
    }
    if (DASHED_DAY_PATTERN.test(text)) {
      const parts = parseDashedDayParts(text);
      return chileMidnightUtcMs(parts.year, parts.month, parts.day);
    }
    // Fail fast for the rest: Date.parse on offset-less strings resolves
    // in host-local time (single-digit days, slash variants the strict
    // branches refuse), so only ISO datetimes carrying their own offset
    // (a T separator plus trailing Z/z or numeric zone) may reach the
    // generic parser. The T requirement rejects bare tokens like '12z'.
    // Mirrors the backend gate; pickers emit moment objects and the API
    // emits zoned ISOs.
    if (!/T.*([zZ]|[+-]\d{2}:?\d{2})$/.test(text)) {
      throw new Error(
        `calendarDay: invalid date value ${JSON.stringify(value)}`
      );
    }
    const parsed = Date.parse(text);
    if (Number.isNaN(parsed)) {
      throw new Error(
        `calendarDay: invalid date value ${JSON.stringify(value)}`
      );
    }
    return parsed;
  }
  if (
    typeof value.valueOf === 'function' &&
    typeof value.isValid === 'function'
  ) {
    if (!value.isValid()) {
      throw new Error('calendarDay: picker value is not a valid date');
    }
    return value.valueOf();
  }
  if (value instanceof Date) {
    const time = value.getTime();
    if (Number.isNaN(time)) {
      throw new Error('calendarDay: picker value is not a valid date');
    }
    return time;
  }
  // No generic valueOf fallback: it would accept boxed Numbers and other
  // exotic objects as epoch millis and silently serialize 1970 dates.
  // Supported inputs are strings, moment-like objects, and Dates above.
  throw new Error(
    `calendarDay: unsupported picker value ${JSON.stringify(value)}`
  );
};

// Normalizes an antd picker value to Chile-midnight ISO, dropping the
// retained base clock time. Picker objects (Moment/dayjs) display a calendar
// day in the browser timezone: that displayed day is what the user picked,
// so it is extracted via .format() and serialized as Chile midnight. This
// is browser-timezone independent by construction. Genuine instants
// (ISO strings, Dates) keep instant semantics: their Chile calendar day
// is serialized. Numbers and booleans are rejected outright: the render
// path must never silently serialize a 1970 date (see tryNormalizePicker).
// Pure: does not mutate the input.
export const normalizePicker = (value) => {
  if (typeof value === 'number' || typeof value === 'boolean') {
    throw new Error(
      `calendarDay: unsupported picker value ${JSON.stringify(value)}`
    );
  }
  // Trim once so padded date-only strings hit the strict branch instead
  // of the host-dependent Date parser (instantMsFromValue trims again
  // for its own branches).
  const text = typeof value === 'string' ? value.trim() : value;
  if (typeof text === 'string' && DAY_PATTERN.test(text)) {
    return parseDayToIso(text);
  }
  if (
    value !== null &&
    typeof value === 'object' &&
    typeof value.format === 'function'
  ) {
    const displayed = value.format('YYYY-MM-DD');
    if (!DAY_PATTERN.test(displayed)) {
      throw new Error(
        `calendarDay: picker value did not display a calendar day ${JSON.stringify(displayed)}`
      );
    }
    return parseDayToIso(displayed);
  }
  const utcMs = instantMsFromValue(value);
  const { year, month, day } = chileDayPartsFromUtcMs(utcMs);
  return new Date(chileMidnightUtcMs(year, month, day)).toISOString();
};

// Non-throwing normalizePicker for render paths (computed dirty checks,
// modal preload). Returns null on invalid input so a bad record date disables
// actions instead of breaking the render. Submit paths must keep using the
// throwing normalizePicker so invalid input surfaces as a validation error.
export const tryNormalizePicker = (value) => {
  try {
    // Epoch numbers and booleans are never legitimate picker or record
    // values; treating them as instants would silently render 1970 dates.
    // NOTE: the backend formatDay deliberately accepts finite epoch millis
    // (staged rows carry them); this render-path guard stays stricter.
    if (typeof value === 'number' || typeof value === 'boolean') return null;
    return normalizePicker(value);
  } catch {
    return null;
  }
};

// Birthday convention note: date-only birthday strings ("YYYY-MM-DD")
// keep their UTC calendar day end to end (formatBirthday), while picker
// preloads normalize through Chile midnight (parseZone). Both yield the
// same displayed day for date-only inputs. New birthday code must pass
// date-only strings, never re-derive days from instants in host time.
// Formats any supported value as "DD/MM/YYYY" in America/Santiago.
// Numbers and booleans are rejected like in normalizePicker: the generic
// valueOf fallback would otherwise serialize epochs as 1969/1970 dates.
export const formatDay = (value) => {
  if (typeof value === 'number' || typeof value === 'boolean') {
    throw new Error(
      `calendarDay: unsupported format value ${JSON.stringify(value)}`
    );
  }
  const text = typeof value === 'string' ? value.trim() : value;
  if (typeof text === 'string' && DAY_PATTERN.test(text)) {
    const { year, month, day } = parseDayParts(text);
    return `${padTo2Digits(day)}/${padTo2Digits(month)}/${year}`;
  }
  if (typeof text === 'string' && SLASH_DAY_PATTERN.test(text)) {
    const { year, month, day } = parseSlashDayParts(text);
    return `${padTo2Digits(day)}/${padTo2Digits(month)}/${year}`;
  }
  if (typeof text === 'string' && DASHED_DAY_PATTERN.test(text)) {
    const { year, month, day } = parseDashedDayParts(text);
    return `${padTo2Digits(day)}/${padTo2Digits(month)}/${year}`;
  }
  const utcMs = instantMsFromValue(value);
  const { year, month, day } = chileDayPartsFromUtcMs(utcMs);
  return `${padTo2Digits(day)}/${padTo2Digits(month)}/${year}`;
};
